import { useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import type { PageSize } from "@/components/common/usePagination";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { formatDealReceivedAt, type DealsListStatus, type DealsPageSize, type DealsTypeMatchFilter } from "@/lib/ops/opsDealsListQuery";
import { OpsDealTypeMatchBadge } from "./OpsDealTypeMatchBadge";
import type { DealRow } from "./opsPaperworkTypes";
import type { OpsDealsListModel } from "./useOpsDealsList";

const selectClass = "mt-1 block w-full min-w-0 rounded border border-gray-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900";

const STATUS_OPTIONS: Array<{ value: DealsListStatus; label: string }> = [
  { value: "active", label: "Active (hide archived)" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

export function OpsPaperworkDealsList(props: {
  list: OpsDealsListModel;
  parentBusy: boolean;
  canCreate: boolean;
  newDealName: string;
  setNewDealName: (v: string) => void;
  setSelectedId: (id: string | null) => void;
  onCreateDeal: () => Promise<void>;
  onSeedTemplates: () => Promise<void>;
}) {
  const list = props.list;
  const busy = props.parentBusy || list.loading;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        {props.canCreate ? (
          <>
            <label className="block text-sm">New deal name
              <input className="mt-1 block w-56 rounded border border-gray-300 px-2 py-1 text-sm" value={props.newDealName} onChange={(e) => props.setNewDealName(e.target.value)} placeholder="Customer / order label" />
            </label>
            <button type="button" disabled={busy} onClick={() => void props.onCreateDeal()} className="rounded bg-indigo-600 text-white text-sm px-3 py-1.5 disabled:opacity-50">Create + seed checklist</button>
            <button type="button" disabled={busy} onClick={() => void props.onSeedTemplates()} className="rounded border text-sm px-3 py-1.5">Seed default templates</button>
          </>
        ) : null}
        <button type="button" disabled={busy} onClick={() => void list.reload()} className="rounded border text-sm px-3 py-1.5">Refresh</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block min-w-0 text-sm">Status
          <select className={selectClass} value={list.status} onChange={(e) => list.setStatus(e.target.value as DealsListStatus)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block min-w-0 text-sm">Salesperson
          <select className={selectClass} value={list.salesperson} onChange={(e) => list.setSalesperson(e.target.value)}>
            <option value="">All</option>
            {list.facets.salespeople.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block min-w-0 text-sm">Office
          <select className={selectClass} value={list.office} onChange={(e) => list.setOffice(e.target.value)}>
            <option value="">All</option>
            {list.facets.offices.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block min-w-0 text-sm">Deal type
          <select className={selectClass} value={list.typeMatch} onChange={(e) => list.setTypeMatch(e.target.value as DealsTypeMatchFilter)}>
            <option value="">All</option>
            <option value="unresolved">Needs match</option>
            <option value="product_only">Product only</option>
            <option value="matched">Matched</option>
          </select>
        </label>
      </div>
      {list.error ? <p className="text-sm text-amber-800">{list.error}</p> : null}
      <PaginationBar
        pageIndex={list.page - 1}
        pageCount={list.pageCount}
        pageSize={list.pageSize as PageSize}
        totalCount={list.total}
        firstShown={list.firstShown}
        lastShown={list.lastShown}
        onPageChange={(i) => list.setPage(i + 1)}
        onPageSizeChange={(n) => {
          if ((list.pageSizeOptions as readonly number[]).includes(n)) list.setPageSize(n as DealsPageSize);
        }}
        pageSizeOptions={list.pageSizeOptions}
        noun="deals"
        alwaysShow
      />
      <ul
        {...{ [FIREFLY_TARGET_ATTR]: "ops-deals-list" }}
        className="divide-y divide-gray-200 rounded-xl border border-gray-200"
      >
        {list.deals.map((d) => (
          <DealListRow key={d.id} deal={d} onOpen={props.setSelectedId} />
        ))}
        {list.deals.length === 0 ? (
          <li className="px-3 py-4 text-sm text-gray-500">
            {list.salesperson || list.office || list.typeMatch
              ? "No deals match these filters."
              : list.status === "active"
                ? props.canCreate
                  ? "No active deals. Status → Archived or All to see cancelled packets."
                  : "No active deals. Accepted orders from Order Intake show up here."
                : list.status === "archived"
                  ? "No archived deals."
                  : "No deals yet. Settings → generate the ShedSuite webhook URL and turn on auto-create, or accept a row from Manufacturing → Order Intake."}
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function DealListRow(props: { deal: DealRow; onOpen: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const deal = props.deal;
  const name = deal.display_name || deal.id.slice(0, 8);
  const serial = (deal.serial ?? "").trim();
  const received = formatDealReceivedAt(deal.created_at);
  const meta = [deal.salesperson_name, deal.office_name, deal.type_match_product_name, deal.type_match_sale_name]
    .filter(Boolean)
    .join(" · ");
  const progress = `${deal.status} · ${deal.paperwork_done ?? 0}/${deal.paperwork_total ?? 0} done${deal.completeness != null ? ` · ${deal.completeness}%` : ""}`;

  return (
    <li className="px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <button type="button" className="min-w-0 text-left" onClick={() => props.onOpen(deal.id)}>
          <span className="font-medium text-gray-900">{name}</span>
          {serial ? <span className="mt-0.5 block text-xs text-gray-500 sm:hidden">{serial}</span> : null}
        </button>
        <button
          type="button"
          className="shrink-0 rounded border px-2 py-0.5 text-xs text-gray-600 sm:hidden"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide" : "Details"}
        </button>
      </div>
      <div className={`${open ? "mt-1" : "mt-1 hidden"} sm:block`}>
        <span className="flex items-center gap-2">
          <OpsDealTypeMatchBadge
            status={deal.type_match_status}
            productName={deal.type_match_product_name}
            saleName={deal.type_match_sale_name}
          />
          {serial ? <span className="hidden text-xs text-gray-500 sm:inline">{serial}</span> : null}
        </span>
        {received ? <span className="block text-xs text-gray-500">Received {received}</span> : null}
        {meta ? <span className="block text-xs text-gray-500">{meta}</span> : null}
        <span className="block text-xs text-gray-500">{progress}</span>
      </div>
    </li>
  );
}
